import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

// Payment Gateway logos
import razorpayLogo from '@/assets/images/payment gateway logo/razorpay.png';
import paytmLogo from '@/assets/images/payment gateway logo/paytm.png';
import phonepeLogo from '@/assets/images/payment gateway logo/phonepay.png';
import gpayLogo from '@/assets/images/payment gateway logo/gpay.jpeg';

// Backend logos
import nodejsLogo from '@/assets/images/backend logo/node-js-.png';
import expressLogo from '@/assets/images/backend logo/express.png';

// Frontend logos
import frontendLogo1 from '@/assets/images/frontend logo/images.png';
import frontendLogo2 from '@/assets/images/frontend logo/images (1).png';
import frontendLogo3 from '@/assets/images/frontend logo/images (3).jpeg';

// Mail & OTP logos
import mailLogo1 from '@/assets/images/mailandotp/mobile_auth.png';
import mailLogo2 from '@/assets/images/mailandotp/Picsart_26-02-06_10-49-10-669.png';
import mailLogo3 from '@/assets/images/mailandotp/Picsart_26-02-06_10-50-32-104.png';

const categories = ['All', 'Payments', 'Backend', 'Frontend', 'Email/SMS'];

// MCP Catalog data with 4 cards
const mcpCatalog = [
  {
    id: 'payment-gateway',
    title: 'Payment Gateway MCP',
    description: 'Production-ready MCP servers for seamless payment gateway integration. Connect Razorpay, Paytm, PhonePe, and other Indian payment providers to your AI-built applications with deterministic, first-run code.',
    logos: [
      { src: razorpayLogo, alt: 'Razorpay' },
      { src: paytmLogo, alt: 'Paytm' },
      { src: phonepeLogo, alt: 'PhonePe' },
      { src: gpayLogo, alt: 'GPay' },
    ],
    tags: ['Payments'],
    category: 'Payments',
    buttonText: 'Use MCP',
    action: 'login',
  },
  {
    id: 'backend',
    title: 'Backend MCP',
    description: 'Build robust server-side logic with MCP servers for APIs, authentication, business logic, and scalable backend architectures. Integrate seamlessly with Node.js, Python, and Go frameworks.',
    logos: [
      { src: nodejsLogo, alt: 'Node.js' },
      { src: expressLogo, alt: 'Express' },
    ],
    tags: ['Backend', 'APIs'],
    category: 'Backend',
    buttonText: 'Join Waitlist',
    action: 'waitlist',
    comingSoon: true,
  },
  {
    id: 'frontend',
    title: 'Frontend MCP',
    description: 'Pre-built MCP servers for UI component libraries, design systems, and frontend state management. Includes Shadcn UI, Tailwind configurations, and React/Next.js integrations verified for production.',
    logos: [
      { src: frontendLogo1, alt: 'React' },
      { src: frontendLogo2, alt: 'Next.js' },
      { src: frontendLogo3, alt: 'Shadcn' },
    ],
    tags: ['Frontend', 'Design'],
    category: 'Frontend',
    buttonText: 'Join Waitlist',
    action: 'waitlist',
    comingSoon: true,
  },
  {
    id: 'mail-otp',
    title: 'Mail & OTP MCP',
    description: 'Verified MCP servers for email delivery, SMS notifications, and OTP authentication flows. Connect Resend, Twilio, MSG91, and other communication providers with zero hallucination.',
    logos: [
      { src: mailLogo1, alt: 'Mobile Auth' },
      { src: mailLogo2, alt: 'Resend' },
      { src: mailLogo3, alt: 'Twilio' },
    ],
    tags: ['Email', 'Auth', 'SMS'],
    category: 'Email/SMS',
    buttonText: 'Join Waitlist',
    action: 'waitlist',
    comingSoon: true,
  },
];

// Individual MCP Card Component
const MCPCard = ({ card, onButtonClick }) => {
  const { title, description, logos, tags, buttonText, comingSoon } = card;

  return (
    <div
      className="mcp-card bg-white dark:bg-[#282929] rounded-xl md:rounded-2xl border border-[#e0e0e0] dark:border-white/15 overflow-hidden flex flex-col h-full transition-all duration-300 ease-out hover:-translate-y-2 hover:shadow-2xl cursor-pointer shadow-[0_4px_12px_rgba(0,0,0,0.08)] dark:shadow-[0_4px_12px_rgba(0,0,0,0.35)]"
      style={{
        transformStyle: 'preserve-3d'
      }}
      data-testid={`mcp-card-${card.id}`}
    >
      {/* Logo Header Strip - Green tinted */}
      <div
        className="px-4 py-3 md:px-5 md:py-4 bg-[#7cb687]/15 dark:bg-[rgba(155,227,143,0.12)]"
      >
        <div className="flex items-center gap-2 md:gap-3 flex-wrap">
          {logos.map((logo, index) => (
            <img
              key={index}
              src={logo.src}
              alt={logo.alt}
              className="rounded-full bg-white border border-[#7cb687]/30 flex-shrink-0 shadow-sm object-contain"
              style={{ width: 32, height: 32 }}
              title={logo.alt}
            />
          ))}
        </div>
      </div>

      {/* Card Body */}
      <div className="p-5 md:p-6 flex flex-col flex-grow">
        <div className="flex items-center gap-2 mb-3">
          <h3 className="text-lg md:text-xl font-semibold text-[#1a1a1a] dark:text-white">
            {title}
          </h3>
        </div>
        <p className="text-sm text-[#666666] dark:text-white/60 leading-relaxed mb-4 flex-grow line-clamp-3">
          {description}
        </p>

        {/* Category Tags */}
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {tags.map((tag, index) => (
              <span
                key={index}
                className="px-3 py-1.5 rounded-full text-xs font-medium bg-[#7cb687]/15 text-[#4a8a54] dark:bg-[rgba(155,227,143,0.12)] dark:text-[#9be38f]"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Action */}
        {comingSoon ? (
          <div
            className="w-full py-3 px-6 rounded-lg font-semibold text-sm text-center bg-[#f0f0f0] text-[#1a1a1a] dark:bg-[#1f1f1f] dark:text-white/80"
          >
            Coming Soon
          </div>
        ) : (
          <button
            onClick={() => onButtonClick(card)}
            className="w-full py-3 px-6 rounded-lg font-semibold text-sm text-white transition-all hover:brightness-110 hover:shadow-lg"
            style={{ background: '#7cb687' }}
          >
            {buttonText}
          </button>
        )}
      </div>
    </div>
  );
};

export const PluginShowcase = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const navigate = useNavigate();

  const handleButtonClick = (card) => {
    if (card.action === 'login') {
      navigate('/early-access');
    } else if (card.action === 'waitlist') {
      const waitlistSection = document.getElementById('join-waitlist');
      if (waitlistSection) {
        waitlistSection.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const filteredCards = activeCategory === 'All'
    ? mcpCatalog
    : mcpCatalog.filter(card => card.category === activeCategory);

  return (
    <section className="bg-[var(--pluckr-bg)] py-12 md:py-24 lg:py-32" data-testid="plugin-showcase-section">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-20">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16 lg:mb-20">
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1a1a1a] dark:text-white leading-[1.1] tracking-tight"
            style={{ fontFamily: "'Martel Sans', sans-serif" }}
          >
            AI MCP Servers <span className="text-[var(--pluckr-green)]">Ready to Deploy</span>
          </h2>
        </div>

        {/* Category Filters - Horizontal scroll on mobile */}
        <div className="mb-8 md:mb-12 -mx-4 md:mx-0 px-4 md:px-0">
          <div className="flex md:flex-wrap gap-3 md:justify-center overflow-x-auto md:overflow-x-visible pb-2 md:pb-0 scrollbar-hide">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`filter-pill flex-shrink-0 px-5 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                  activeCategory === category
                    ? 'bg-[#7cb687] text-white'
                    : 'bg-[#f0f0f0] text-[#5a5a5a] hover:bg-[#e0e0e0] dark:bg-[#282929] dark:text-[#e0e0e0] dark:hover:bg-[#1f1f1f]'
                }`}
                data-testid={`filter-${category.toLowerCase().replace(/\//g, '-').replace(/\s+/g, '-')}`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* MCP Catalog Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 lg:gap-8">
          {filteredCards.map((card) => (
            <MCPCard key={card.id} card={card} onButtonClick={handleButtonClick} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default PluginShowcase;
